export function formatPostDate(isoDate) {
	const parsedDate = new Date(`${isoDate}T00:00:00`);
	return parsedDate.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export const posts = [
	{
		slug: "benefits-of-apprentices-for-software-projects",
		title: `Benefits of Using Apprentices for Your Software Development Projects`,
		date: "2024-11-12",
		author: "Chad Greer",
		category: "Apprenticeships",
		excerpt: `One way to stay competitive in today's fast-paced tech industry is to integrate apprentices from Lucentary Academy into your software development projects and staff — unlocking a host of benefits for your business.`,
		image: "https://images.pexels.com/photos/12899191/pexels-photo-12899191.jpeg?auto=compress&cs=tinysrgb&w=1600",
		imageAlt: "Two software developers collaborating on code at a laptop in a modern office",
		content: [
			{
				type: "paragraph",
				text: `In today's fast-paced tech industry, staying competitive means not only leveraging the latest technologies but also building a talented, skilled workforce capable of driving innovation and solving complex problems. One way to meet this challenge is by integrating apprentices from Lucentary Academy into your software development projects and staff. Lucentary Academy specializes in training the next generation of software developers, providing them with hands-on experience, cutting-edge technical skills, and a deep understanding of industry standards. By partnering with Lucentary Academy for your apprentice needs, you can unlock a host of benefits for your business.`,
			},
			{
				type: "paragraph",
				text: `In this blog post, we'll explore the advantages of using apprentices from Lucentary Academy and how they can positively impact your software development efforts.`,
			},
			{
				type: "heading",
				text: `1. Access to Well-Trained, Ready-to-Work Talent`,
			},
			{
				type: "paragraph",
				text: `Lucentary Academy is known for its rigorous training programs that equip apprentices with the technical knowledge and practical experience needed to excel in software development roles. From front-end development to back-end programming, data structures to cloud computing, Lucentary's curriculum is designed to mirror real-world projects, ensuring that apprentices are well-prepared to hit the ground running.`,
			},
			{
				type: "paragraph",
				text: `When you hire an apprentice from Lucentary Academy, you're bringing on board someone who has already gained a solid foundation in modern programming languages, frameworks, and development tools. This means you don't have to spend months getting them up to speed. Instead, they can immediately contribute to your ongoing projects, adding value right from the start.`,
			},
			{
				type: "heading",
				text: `2. Cost-Effective Talent Acquisition`,
			},
			{
				type: "paragraph",
				text: `Hiring experienced software developers can be expensive, particularly when you're looking for highly specialized skills or niche expertise. Lucentary apprentices, on the other hand, represent a cost-effective alternative. Apprentices typically come at a lower salary than fully qualified developers, which allows you to manage your staffing costs more effectively without sacrificing quality.`,
			},
			{
				type: "paragraph",
				text: `Moreover, apprenticeships offer a unique opportunity to build a talent pipeline for your business. By hiring apprentices from Lucentary Academy, you're making an investment in the future of your company. With time, they will grow into highly skilled and loyal employees who are familiar with your company culture, processes, and technology stack—ultimately saving you money in the long run on recruitment, training, and turnover costs.`,
			},
			{
				type: "heading",
				text: `3. Faster Time to Market on Projects`,
			},
			{
				type: "paragraph",
				text: `Software development projects often face the challenge of tight deadlines and resource constraints. By utilizing Lucentary apprentices, you can augment your development team with motivated, eager-to-learn individuals who can support various aspects of the development process. Apprentices can assist with coding tasks, testing, debugging, documentation, and even contributing fresh ideas for new features or solutions.`,
			},
			{
				type: "paragraph",
				text: `Because Lucentary apprentices are trained to work in teams and accustomed to tackling real-world problems, they can quickly integrate into your existing workflow and help move projects forward faster. Their ability to handle well-defined tasks allows your more experienced developers to focus on high-level architecture, complex problem-solving, or innovation, increasing overall productivity and improving time-to-market.`,
			},
			{
				type: "heading",
				text: `4. Diverse Perspectives and Innovation`,
			},
			{
				type: "paragraph",
				text: `Apprentices, especially those from a forward-thinking institution like Lucentary Academy, bring new and diverse perspectives to the table. Many of these individuals are passionate about technology and eager to solve problems in creative ways. They are often exposed to the latest trends and best practices in software development, which means they can introduce fresh ideas and innovative solutions to your projects.`,
			},
			{
				type: "paragraph",
				text: `Incorporating apprentices into your team can spark a culture of innovation. As they work closely with senior developers and more experienced team members, apprentices can challenge assumptions, propose alternative solutions, and introduce new technologies or frameworks they've recently encountered. This injection of creativity can be the catalyst for new features, improvements to existing processes, or the exploration of emerging technologies that might otherwise be overlooked.`,
			},
			{
				type: "heading",
				text: `5. Flexible and Scalable Staffing Solution`,
			},
			{
				type: "paragraph",
				text: `Apprenticeships offer a unique flexibility that can be particularly valuable for businesses that need to scale their development teams up or down. If you have fluctuating workloads or short-term projects that require additional manpower, apprentices can provide a scalable staffing solution. Whether you're working on a large software overhaul, building a new app, or adding new features to an existing product, Lucentary apprentices can jump in and provide support where needed.`,
			},
			{
				type: "paragraph",
				text: `Additionally, as apprentices learn and grow on the job, you can gradually increase their responsibilities, giving them a chance to take on more complex tasks as their skills develop. This scalability allows you to tailor the apprenticeship experience to the changing needs of your business, making it a highly adaptable resource for your team.`,
			},
			{
				type: "heading",
				text: `6. Fostering Company Loyalty and Reducing Turnover`,
			},
			{
				type: "paragraph",
				text: `The value of apprenticeship programs goes beyond simply training individuals—it's about building a long-term relationship with a future employee. Apprentices from Lucentary Academy are typically highly motivated to succeed, knowing that they have been given a chance to prove their abilities in a real-world setting.`,
			},
			{
				type: "paragraph",
				text: `When you bring in apprentices from Lucentary, you're not just training them for a job—you're creating a potential long-term employee who is familiar with your company's culture, processes, and goals. Over time, apprentices who feel supported and valued are more likely to stay with your company after their apprenticeship ends. This can help reduce turnover rates, ensuring that you retain top talent who are committed to the growth and success of your business.`,
			},
			{
				type: "heading",
				text: `7. Mentorship Opportunities for Your Team`,
			},
			{
				type: "paragraph",
				text: `Apprenticeships provide an excellent opportunity for your senior staff to engage in mentorship and leadership roles. Experienced developers can take on the responsibility of guiding and mentoring apprentices, which not only helps the apprentices learn and grow but also fosters a stronger, more collaborative company culture.`,
			},
			{
				type: "paragraph",
				text: `Mentorship can have reciprocal benefits as well. Senior staff members who mentor apprentices often develop new leadership and teaching skills that are essential for career progression. It can also boost team morale, as employees feel a sense of pride and fulfillment in helping to shape the next generation of developers.`,
			},
			{
				type: "heading",
				text: `8. Strong Support from Lucentary Academy`,
			},
			{
				type: "paragraph",
				text: `When you partner with Lucentary Academy, you gain access to more than just talented apprentices—you also receive ongoing support from the academy itself. Lucentary works closely with businesses to ensure that apprentices are set up for success, offering additional resources, training, and guidance when needed. This partnership ensures that both your business and the apprentice are fully supported throughout the duration of the program.`,
			},
			{
				type: "paragraph",
				text: `Furthermore, Lucentary's focus on professional development ensures that apprentices continue to evolve and refine their skills in-line with industry trends, making them even more valuable to your team as time goes on.`,
			},
			{
				type: "heading",
				text: `Conclusion: A Strategic Investment in Your Future`,
			},
			{
				type: "paragraph",
				text: `Utilizing apprentices from Lucentary Academy for your software development projects and staff is a win-win for your business. Not only do you gain access to a fresh pool of talent eager to contribute, but you also benefit from cost-effective, highly trained individuals who are primed to make an impact. Apprentices bring diversity, innovation, and creativity to your projects, helping to move them forward more efficiently while allowing senior team members to focus on strategic goals.`,
			},
			{
				type: "paragraph",
				text: `By investing in apprentices from Lucentary Academy, you're not just filling a position—you're building a long-term talent pipeline, fostering company loyalty, and positioning your business for success in an ever-changing tech landscape. Whether you're looking to expand your team, accelerate projects, or tap into new ideas, Lucentary apprentices can be a valuable asset to your business.`,
			},
		],
	},
	{
		slug: "good-unit-tests-criteria-and-questions",
		title: `3 Criteria and 5 Questions for Good Unit Tests`,
		date: "2024-04-15",
		author: "Chad Greer",
		category: "Software Craft",
		excerpt: `Do you write good unit tests? If you believe so, then how do you know they're good? It's easy to fall victim to confirmation and self-serving biases. So how can you objectively determine if you're writing good ones?`,
		image: "https://images.pexels.com/photos/6424584/pexels-photo-6424584.jpeg?auto=compress&cs=tinysrgb&w=1600",
		imageAlt: "Source code displayed in a code editor on a dark screen",
		content: [
			{
				type: "paragraph",
				text: `Do you write good unit tests? If you believe so, then how do you know they're good? It's easy for us to fall victim to confirmation and self-serving biases because there are so many bad examples of software developers in our industry. So, how can you objectively determine if you're writing good unit tests?`,
			},
			{
				type: "paragraph",
				text: `It turns out there are 3 criteria for identifying good unit tests:`,
			},
			{
				type: "list",
				items: [
					`They're Automated`,
					`They Verify Behavior`,
					`They Support Refactoring`,
				],
			},
			{
				type: "paragraph",
				text: `And that's about it! These seem simple enough, but how can you tell if your unit tests meet these three criteria?`,
			},
			{
				type: "heading",
				text: `They're Automated`,
			},
			{
				type: "paragraph",
				text: `Tests should be automated, but automated to what degree exactly? The first question to answer is this: "Is a human executing the system or component in order to verify behavior?" If so, then you're not automated at all. It was Robert C. Martin that said "manual testing is immoral."`,
			},
			{
				type: "paragraph",
				text: `The second question to answer is "are your tests executed on every check-in, or at least daily?" This is getting into the DevOps practices of Continuous Integration and Continuous Delivery, but your tests should not require a human to kick them off; they should run as a part of your delivery pipeline. These tests can run as a part of the Build, as a Post-Build validation step, and/or Post-Deployment (by running in a target environment after the system is deployed). You'll likely need a combination of these approaches to achieve the level of confidence your team requires, although unit tests are typically executed in the first two parts of your deployment pipeline: Build or Post-Build.`,
			},
			{
				type: "paragraph",
				text: `The third question you'll want to answer is "can your tests be executed in any environment?" Good unit tests become a fantastic way to exercise the system to ensure everything works as expected (can someone say 'regression testing?'). Being able to execute tests in any environment can be a difficult goal to achieve, but it is an ideal worth exploring. If you can truly design your tests to run in any environment, you minimize the likelihood of the dreaded "Works on My Machine" mantra of development.`,
			},
			{
				type: "heading",
				text: `They Verify Behavior`,
			},
			{
				type: "paragraph",
				text: `There are two human philosophies which help us prove things to be true or false. Mathematics (specifically mathematical proofs) can help us prove something to be true and correct, while Science (and the scientific method) can help us prove something to be false. Tests are much more like science than mathematics: testing cannot prove the absence of bugs (which is proving a negative), it can only prove that the software behaves, or doesn't behave, as expected for anticipated inputs or behaviors (assuming your system is deterministic to a large degree).`,
			},
			{
				type: "paragraph",
				text: `The fourth question to answer is "do your tests verify that the system is designed correctly, or do they verify that the system works as the user expects?" Tests that verify that the system is designed correctly are largely waste (more on this in They Support Refactoring). Ultimately what is "correct" is highly subjective, and the definition of "correct" in any situation will likely change for a myriad of reasons, including the growth of developers, changes in policy, and the evolution of our technology stack, just to mention a few.`,
			},
			{
				type: "paragraph",
				text: `Instead, we should focus on verifying that the system functions the way our users expect. If you're operating in an Agile environment, there's good news: you likely already have a source of these expectations! The source is the acceptance criteria from your user stories. Certainly, good acceptance criteria don't spell out the exact test cases you need to write (since good acceptance criteria are negotiable by definition), but good acceptance criteria give us the foundation for writing good test cases.`,
			},
			{
				type: "paragraph",
				text: `From a Lean perspective, we define waste as anything that does not provide value to the customer or user. The customer or user wants to know that the system operates as expected for them, and frankly, they couldn't care less about what design or architectural patterns we chose to implement. Don't waste your time (and therefore their investment in us) by testing the design of your system (such as your public API or the implementation of specific classes or methods). Instead, provide value by ensuring your unit tests verify the functionality as identified by the acceptance criteria.`,
			},
			{
				type: "heading",
				text: `They Support Refactoring`,
			},
			{
				type: "paragraph",
				text: `The ability to support refactoring is the hardest criteria to meet when writing good unit tests. Too many unit tests are written which actually solidify the initial design of the system and therefore make it more difficult to change the code. Remember, as Martin Fowler told us, "refactoring is a change made to the internal structure of the code that doesn't modify its observable behavior." In other words, change the design, but don't change the behavior.`,
			},
			{
				type: "paragraph",
				text: `So, the fifth, and arguably most important, question to answer is this: "do your tests allow you to make wholescale changes and refactorings without causing you to spend equal or more time changing the tests?" If you create a class with a switch statement, and now you want to introduce an inheritance hierarchy, can you make that change by making at most one change in the test code? If you change the type of a reference from an array to a Sequence or a List, can you do that without changing your tests? If you move a method from one class to another or change a field to a method, do you tests allow you to do that without changing the reference in more than one or two places in the test code?`,
			},
			{
				type: "paragraph",
				text: `This is the hard part! We need to find a balance in our unit tests where they verify specific policies and behaviors but they aren't tightly-coupled to the design of the code base under test. There are a number of patterns and approaches for doing this, but it may require you to rethink how you approach creating unit tests.`,
			},
			{
				type: "heading",
				text: `In Summary`,
			},
			{
				type: "paragraph",
				text: `Your goal is to be able to answer these 5 questions:`,
			},
			{
				type: "list",
				items: [
					`Is a human executing the system or component in order to verify behavior? The ideal answer is "no".`,
					`Are your tests executed on every check-in, or at least daily? The ideal answer is "yes".`,
					`Can your tests be executed in any environment? The ideal answer is "no".`,
					`Do your tests verify that the system is designed correctly, or do they verify that the system works as the user expected? The ideal answer is "they verify that the system works as the user expected".`,
					`Do your tests allow you to make wholescale changes and refactorings without causing you to spend equal or more time changing the tests? The ideal answer is "yes".`,
				],
			},
			{
				type: "paragraph",
				text: `Do you have questions about how to do this? Or, do you disagree with me completely? I'd love to hear from you!`,
			},
		],
	},
	{
		slug: "an-honest-privacy-policy-satire",
		title: `An Honest Privacy Policy (Satire)`,
		date: "2024-04-02",
		author: "Chad Greer",
		category: "Satire",
		excerpt: `This Privacy Policy concerns the site, application, or system provided by WeSaySo Corporation Inc. By using it, you agree to let us use your personal and sensitive information any way we want — and we'll sell it every chance we get. (Satire.)`,
		image: "https://images.pexels.com/photos/39624/padlock-lock-chain-key-39624.jpeg?auto=compress&cs=tinysrgb&w=1600",
		imageAlt: "A padlock with a key, representing data privacy and security",
		content: [
			{
				type: "paragraph",
				text: `This Privacy Policy concerns the site, application, or system provided by WeSaySo Corporation Inc. By using our site, application, or system, you agree to give us permission to use your personal and sensitive information in any way that we want. We will sell your information to other companies every chance we get in order to make money from your personal information.`,
			},
			{
				type: "paragraph",
				text: `We will do our "best" to keep your information secret from companies and individuals that aren't paying us for your information. However, due to budget constraints and a desire to chase increasing shareholder value, the security of your data will take a back seat to every other initiative that looks profitable to our shareholders (whether it's actually profitable or not). Inevitably, of course, we will be large enough to be a target for hackers and criminals, and your information will be shared with people and companies that didn't pay us for the information, much to our chagrin.`,
			},
			{
				type: "paragraph",
				text: `If you don't agree to these terms, you may choose to NOT use our site, application, or system. However, we know that's a silly statement since you need our software to continue engaging with others that have already given us their personal and sensitive information.`,
			},
			{
				type: "paragraph",
				text: `Good luck, and enjoy the endless spam emails and phone calls you will get from us making money off of your data!`,
			},
		],
	},
	{
		slug: "what-is-lucentary-all-about",
		title: `What is Lucentary All About?`,
		date: "2024-04-02",
		author: "Chad Greer",
		category: "Company",
		excerpt: `I've been in software for over two decades and helped almost 60 companies with over 200 projects. Now I'm ready to help others have the kind of success I've enjoyed. Lucentary Academy is my attempt to do just that.`,
		image: "https://images.pexels.com/photos/7988208/pexels-photo-7988208.jpeg?auto=compress&cs=tinysrgb&w=1600",
		imageAlt: "A mentor teaching a small team using a laptop",
		content: [
			{
				type: "heading",
				text: `About Chad Greer`,
			},
			{
				type: "paragraph",
				text: `I've been in software for over two decades now. I've helped almost 60 different companies with over 200 different projects (if that sounds like a lot, that's because it is). I've made a great living for myself, and I've helped a lot of people. Now, I'm ready to change my lifestyle so I can help others have the kind of success I've enjoyed. Lucentary Academy is my attempt to do just that.`,
			},
			{
				type: "heading",
				text: `About Lucentary Academy`,
			},
			{
				type: "paragraph",
				text: `The programs I teach here are more than just "how to code". I take people through the practices and disciplines that separate the best developers from the average. You're learning more than just how to code here: you're learning how to be a great software developer. That's my guarantee.`,
			},
			{
				type: "paragraph",
				text: `The courses are in-person, hands-on, and self-paced. You can start at any time, and students aren't locked in with contracts, long semesters, or cancellation fees.`,
			},
			{
				type: "paragraph",
				text: `Get started today by visiting our Contact page or calling 817-704-0300.`,
			},
		],
	},
];